/**
 * Mock alerts for one vehicle, to try the alert features without a backend:
 * Cold Truck 4 (`truck-04`) fires one every 40 minutes around the clock,
 * cycling through speeding, a warm cargo, a door left open, a long stop and
 * two driver-monitoring camera alarms (phone use, smoking).
 * The schedule is absolute, so the same alert (same id and time) shows in
 * its playback (pinned to the route there) and in its Alert History (the
 * details panel's Alerts tab, and opening one anywhere) -- see the mock
 * handlers in web/src/mocks/trackingDemoAlerts.ts.
 */

export const DEMO_ALERTS_VEHICLE_ID = "truck-04";

const MINUTE_MS = 60_000;
const STEP_MS = 40 * MINUTE_MS;
/** Off the hour, so alerts don't line up with the mock route's blocks. */
const OFFSET_MS = 7 * MINUTE_MS;
const ID_PREFIX = "demo-alert-";

type DemoKind = "speeding" | "warmCargo" | "phoneUse" | "doorOpen" | "longStop" | "smoking";
const KINDS: readonly DemoKind[] = [
  "speeding",
  "warmCargo",
  "phoneUse",
  "doorOpen",
  "longStop",
  "smoking",
];

/** A few places with an address, and every third with none (so the
 *  coordinates show instead, as the backend advises). */
const PLACES: readonly ({ readonly en: string; readonly ar: string } | null)[] = [
  { en: "King Khalid Rd, Ad Diriyah", ar: "طريق الملك خالد، الدرعية" },
  { en: "Al Uyaynah, Riyadh Province", ar: "العيينة، منطقة الرياض" },
  null,
  { en: "Northern Ring Rd, Riyadh", ar: "الطريق الدائري الشمالي، الرياض" },
  { en: "Exit 2, Salbukh Rd", ar: "مخرج 2، طريق صلبوخ" },
  null,
];

/** Near the truck's live position (24.909, 46.362). */
const HOME = { latitude: 24.909, longitude: 46.362 };

export interface DemoAlert {
  readonly id: string;
  readonly occurredMs: number;
  readonly occurredAt: string;
  readonly alertType: "SPEED" | "SENSOR" | "DOWNTIME" | "DRIVER_BEHAVIOUR";
  readonly alertNameEn: string;
  readonly alertNameAr: string;
  readonly triggeredValueEn: string;
  readonly triggeredValueAr: string;
  /** Where Alert History says it fired (playback pins it to its route). */
  readonly location: { readonly latitude: number; readonly longitude: number };
  readonly locationLabel: { readonly en: string; readonly ar: string } | null;
  /** Its Alert History details (`details` of `GET /alerts/history/:id`). */
  readonly details: Readonly<Record<string, unknown>>;
}

function demoAlertAt(step: number): DemoAlert {
  const occurredMs = step * STEP_MS + OFFSET_MS;
  const kind = KINDS[((step % KINDS.length) + KINDS.length) % KINDS.length]!;
  const wobble = (step % 7) / 7;
  const base = {
    id: `${ID_PREFIX}${String(occurredMs)}`,
    occurredMs,
    occurredAt: new Date(occurredMs).toISOString(),
    location: {
      latitude: HOME.latitude + 0.02 * Math.sin(step),
      longitude: HOME.longitude + 0.02 * Math.cos(step),
    },
    locationLabel: PLACES[((step % PLACES.length) + PLACES.length) % PLACES.length] ?? null,
  };
  switch (kind) {
    case "speeding": {
      const speed = 96 + Math.round(wobble * 20);
      return {
        ...base,
        alertType: "SPEED",
        alertNameEn: "Speeding over 90 km/h",
        alertNameAr: "تجاوز السرعة 90 كم/س",
        triggeredValueEn: `${String(speed)} km/h`,
        triggeredValueAr: `${String(speed)} كم/س`,
        details: { type: "SPEED", recordedSpeedKph: speed, speedLimitKph: 90 },
      };
    }
    case "warmCargo": {
      const temperature = 9 + Math.round(wobble * 40) / 10;
      return {
        ...base,
        alertType: "SENSOR",
        alertNameEn: "Cargo too warm",
        alertNameAr: "ارتفاع حرارة الحمولة",
        triggeredValueEn: `${String(temperature)} °C`,
        triggeredValueAr: `${String(temperature)} °م`,
        details: {
          type: "SENSOR",
          sensor: {
            id: "sensor-truck-04-temp",
            nameEn: "Cargo temperature",
            nameAr: "حرارة الحمولة",
            sensorType: "TEMPERATURE",
          },
          quantity: "TEMPERATURE",
          triggeredValue: temperature,
          unit: "°C",
          condition: { operator: "GREATER_THAN", value: 8 },
        },
      };
    }
    case "doorOpen":
      return {
        ...base,
        alertType: "SENSOR",
        alertNameEn: "Cargo door opened",
        alertNameAr: "فتح باب الحمولة",
        triggeredValueEn: "Open",
        triggeredValueAr: "مفتوح",
        details: {
          type: "SENSOR",
          sensor: {
            id: "sensor-truck-04-door",
            nameEn: "Cargo door",
            nameAr: "باب الحمولة",
            sensorType: "DOOR_STATUS",
          },
          quantity: "STATE",
          triggeredValue: 1,
          unit: null,
          condition: { operator: "IS", value: 1 },
        },
      };
    case "phoneUse":
      return {
        ...base,
        alertType: "DRIVER_BEHAVIOUR",
        alertNameEn: "Phone use while driving",
        alertNameAr: "استخدام الهاتف أثناء القيادة",
        triggeredValueEn: "Phone use",
        triggeredValueAr: "استخدام الهاتف",
        details: {
          type: "DRIVER_BEHAVIOUR",
          alarmType: 2,
          behaviourEn: "Phone use",
          behaviourAr: "استخدام الهاتف",
          level: 2,
          driver: { id: "driver-truck-04", nameEn: "Khalid Saeed", nameAr: "خالد سعيد" },
          driverMonitoringEventId: `dms-${String(occurredMs)}`,
        },
      };
    case "smoking":
      return {
        ...base,
        alertType: "DRIVER_BEHAVIOUR",
        alertNameEn: "Smoking while driving",
        alertNameAr: "التدخين أثناء القيادة",
        triggeredValueEn: "Smoking",
        triggeredValueAr: "التدخين",
        details: {
          type: "DRIVER_BEHAVIOUR",
          alarmType: 3,
          behaviourEn: "Smoking",
          behaviourAr: "التدخين",
          level: 1,
          driver: { id: "driver-truck-04", nameEn: "Khalid Saeed", nameAr: "خالد سعيد" },
          driverMonitoringEventId: `dms-${String(occurredMs)}`,
        },
      };
    case "longStop": {
      const minutes = 25 + Math.round(wobble * 35);
      return {
        ...base,
        alertType: "DOWNTIME",
        alertNameEn: "Stopped too long",
        alertNameAr: "توقف طويل",
        triggeredValueEn: `${String(minutes)} min`,
        triggeredValueAr: `${String(minutes)} دقيقة`,
        details: { type: "DOWNTIME", durationMinutes: minutes },
      };
    }
  }
}

/** The demo vehicle's alerts in a period (both ends inclusive), oldest
 *  first. */
export function demoAlertsBetween(fromMs: number, toMs: number): DemoAlert[] {
  if (!(toMs >= fromMs)) return [];
  const first = Math.ceil((fromMs - OFFSET_MS) / STEP_MS);
  const last = Math.floor((toMs - OFFSET_MS) / STEP_MS);
  const alerts: DemoAlert[] = [];
  for (let step = first; step <= last; step += 1) alerts.push(demoAlertAt(step));
  return alerts;
}

/** One demo alert by id, or `undefined` for any other id. */
export function demoAlertById(id: string): DemoAlert | undefined {
  if (!id.startsWith(ID_PREFIX)) return undefined;
  const occurredMs = Number(id.slice(ID_PREFIX.length));
  const step = (occurredMs - OFFSET_MS) / STEP_MS;
  return Number.isInteger(step) ? demoAlertAt(step) : undefined;
}
